/**
 * Pure helpers for the optional, user-entered session outcome check-in.
 *
 * This module deliberately records a subjective report and the playback
 * condition that was present when it was entered. It does not infer an effect
 * from a waveform or turn a rating into an efficacy claim.
 */

export const RELAXATION_OUTCOME_METRIC = "relaxation_0_10";
export const MESSAGE_DETECTION_METRIC = "message_detection_0_2";
/**
 * Binary self-report of whether the session actually produced trance.
 *
 * This is deliberately separate from `relaxation_0_10`: a subject can be deeply
 * relaxed and completely un-hypnotised, and conflating the two is how a system
 * convinces itself it is working when it is not. `1` means the subject reports
 * being hypnotised; `0` means they stayed awake. It is a subjective report, not
 * a measured effect, and it is never inferred from a waveform.
 */
export const TRANCE_OUTCOME_METRIC = "trance_confirmed_0_1";
export const DETECTION_RESPONSE_LABELS = Object.freeze(["no", "unsure", "yes"]);
export const OUTCOME_REPORT_PHASES = Object.freeze([
  "baseline",
  "during_or_after",
  "unspecified",
]);

export function normalizeOutcomeRating(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(10, Math.round(numeric)));
}

/** Keep detection as an explicit three-state self-report, not a guessed flag. */
export function normalizeDetectionResponse(value) {
  const numeric = Number(value);
  if (!Number.isInteger(numeric) || numeric < 0 || numeric > 2) return null;
  return numeric;
}

/**
 * Normalise the binary trance report. Accepts booleans and 0/1 only, so a
 * malformed payload can never be silently recorded as a success.
 */
export function normalizeTranceResponse(value) {
  if (value === true) return 1;
  if (value === false) return 0;
  const numeric = Number(value);
  if (!Number.isInteger(numeric) || (numeric !== 0 && numeric !== 1)) return null;
  return numeric;
}

export function detectionResponseLabel(value) {
  const numeric = normalizeDetectionResponse(value);
  return numeric === null ? "unspecified" : DETECTION_RESPONSE_LABELS[numeric];
}

/** Resolve timing at the instant the listener submits a rating. */
export function resolveOutcomeReportPhase({ sessionStarted = null } = {}) {
  if (sessionStarted === true) return "during_or_after";
  if (sessionStarted === false) return "baseline";
  return "unspecified";
}

function normalizeOutcomeReportPhase(value) {
  const phase = String(value || "").trim().toLowerCase();
  return OUTCOME_REPORT_PHASES.includes(phase) ? phase : "unspecified";
}

function finiteNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function scalarLayerSnapshot(layerStatus = {}) {
  const keys = [
    "audible_rhythm_active",
    "audible_rhythm_phase",
    "audible_rhythm_tick_index",
    "audible_rhythm_bpm",
    "binaural",
    "ripple",
    "isochronic",
    "shared_bass_ripple",
    "heartbeat_audio",
    "breath_sound",
    "brainwave_target_hz",
    "ripple_hz",
  ];
  return Object.fromEntries(keys
    .filter((key) => layerStatus?.[key] !== undefined && layerStatus?.[key] !== null)
    .map((key) => [key, layerStatus[key]]));
}

/**
 * Convert live player state into the backend's scalar-only outcome context.
 * Keep this intentionally small and stable so reports remain comparable.
 */
export function buildOutcomeContext({
  track = null,
  elapsedSessionSeconds = 0,
  playbackState = "unknown",
  layerStatus = {},
  arcStage = "",
  condition = {},
  reportPhase = "unspecified",
  transportProfile = null,
} = {}) {
  const elapsed = Math.max(0, Math.round(finiteNumber(elapsedSessionSeconds) * 10) / 10);
  const snapshot = scalarLayerSnapshot(layerStatus);
  const block = condition?.block;
  const normalizedBlock = Number.isFinite(Number(block))
    ? Number(block)
    : String(block || "unassigned");
  const context = {
    condition_id: String(condition?.id || "custom_session"),
    condition_block: normalizedBlock,
    condition_label: String(condition?.label || "Custom session"),
    condition_schema: String(condition?.schemaVersion || "outcome-context.v1"),
    report_phase: normalizeOutcomeReportPhase(reportPhase),
    track_file: String(track?.file || ""),
    track_title: String(track?.title || ""),
    elapsed_session_seconds: elapsed,
    playback_state: String(playbackState || "unknown"),
    arc_stage: String(arcStage || ""),
    layer_status_json: JSON.stringify(snapshot),
  };
  if (transportProfile && typeof transportProfile === "object") {
    context.transport_profile_json = JSON.stringify(transportProfile);
  }
  return context;
}
